import { useEffect, useState } from 'react';
import { Box, Group, Stack, Text, Title } from '@mantine/core';
import { useKiosk } from '@/providers/kioskState';
import type { InfoVariant } from '@/providers/kioskState';
import { playBleep } from '@/utils/audio';

const DISMISS_DELAYS: Record<InfoVariant, number> = {
  error: 5000,
  warning: 4000,
  info: 3000,
};

const VARIANT_CONFIG: Record<InfoVariant, {
  heading: string;
  colour: string;
  glowColour: string;
  bgTint: string;
  iconBg: string;
}> = {
  error: {
    heading: 'Error',
    colour: 'var(--cmd-error)',
    glowColour: 'var(--cmd-error-glow)',
    bgTint: 'rgba(239, 68, 68, 0.15)',
    iconBg: 'linear-gradient(135deg, var(--cmd-error) 0%, #DC2626 100%)',
  },
  warning: {
    heading: 'Notice',
    colour: 'var(--amber-accent)',
    glowColour: 'rgba(245, 158, 11, 0.25)',
    bgTint: 'rgba(245, 158, 11, 0.15)',
    iconBg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  },
  info: {
    heading: 'Info',
    colour: 'var(--cmd-accent)',
    glowColour: 'var(--cmd-accent-glow)',
    bgTint: 'rgba(59, 130, 246, 0.15)',
    iconBg: 'linear-gradient(135deg, var(--cmd-accent) 0%, #2563EB 100%)',
  },
};

export function InfoMessageScreen() {
  const { state, reset } = useKiosk();
  const variant = state.infoVariant ?? 'error';
  const config = VARIANT_CONFIG[variant];
  const delay = DISMISS_DELAYS[variant];
  const [timeLeft, setTimeLeft] = useState(delay / 1000);

  // Audio cue on mount (I22 — same rationale as SuccessScreen).
  useEffect(() => {
    playBleep(variant === 'error' ? 'error' : variant === 'warning' ? 'warning' : 'warning');
  }, [variant]);

  // Auto-reset after delay
  useEffect(() => {
    const timer = setTimeout(reset, delay);
    return () => clearTimeout(timer);
  }, [reset, delay]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 0.1));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const circumference = 2 * Math.PI * 88;
  const progress = (timeLeft / (delay / 1000)) * circumference;

  return (
    <Stack
      align="center"
      gap={40}
      onClick={reset}
      py={24}
      style={{ cursor: 'pointer' }}
    >
      {/* Icon with Countdown Ring */}
      <Box
        style={{
          position: 'relative',
          width: 'min(200px, 45vw)',
          height: 'min(200px, 45vw)',
        }}
      >
        {/* Countdown ring */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 200 200"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'rotate(-90deg)',
          }}
        >
          <circle
            cx="100"
            cy="100"
            r="88"
            fill="none"
            stroke={config.bgTint}
            strokeWidth="6"
          />
          <circle
            cx="100"
            cy="100"
            r="88"
            fill="none"
            stroke={config.colour}
            strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.1s linear',
              filter: `drop-shadow(0 0 6px ${config.glowColour})`,
            }}
          />
        </svg>

        {/* Centering wrapper */}
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '60%',
          }}
        >
          {/* Icon circle */}
          <Box
            className={variant === 'error' ? 'kiosk-error-shake--glow' : ''}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: config.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 32px ${config.glowColour}, 0 2px 8px rgba(0, 0, 0, 0.10)`,
            }}
          >
            {variant === 'error' ? (
              /* Exclamation in circle */
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" style={{ strokeDasharray: 63, strokeDashoffset: 63, animation: 'x-draw 0.5s var(--ease-out) 0.1s forwards' }} />
                <line x1="12" y1="8" x2="12" y2="12" style={{ strokeDasharray: 4, strokeDashoffset: 4, animation: 'x-draw 0.3s var(--ease-out) 0.4s forwards' }} />
                <line x1="12" y1="16" x2="12.01" y2="16" style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 'x-draw 0.2s var(--ease-out) 0.6s forwards' }} />
              </svg>
            ) : variant === 'warning' ? (
              /* Warning triangle */
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            ) : (
              /* Info icon */
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            )}
          </Box>
        </Box>
      </Box>

      {/* Message */}
      <Stack align="center" gap="md" mt={8}>
        <Title
          order={1}
          ta="center"
          fw={700}
          style={{
            fontFamily: 'var(--font-heading)',
            color: config.colour,
            fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
            letterSpacing: '-0.02em',
          }}
        >
          {config.heading}
        </Title>

        <Text
          ta="center"
          maw={480}
          fw={500}
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: '1.0625rem',
            lineHeight: 1.6,
          }}
        >
          {state.infoMessage}
        </Text>

        {state.infoDetails && (
          <Text
            ta="center"
            maw={480}
            fw={500}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
          >
            {state.infoDetails}
          </Text>
        )}
      </Stack>

      {/* Hint Bar */}
      <Box
        mt={16}
        style={{
          padding: 'var(--space-3) var(--space-6)',
          borderRadius: 'var(--radius-full)',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-colour)',
        }}
      >
        <Group gap={8}>
          <Box
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: config.colour,
              boxShadow: `0 0 8px ${config.glowColour}`,
              animation: 'kioskPulse 1.5s var(--ease-in-out) infinite',
            }}
          />
          <Text
            fw={500}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              fontSize: '0.8125rem',
            }}
          >
            Tap anywhere to continue or wait {Math.ceil(timeLeft)}s
          </Text>
        </Group>
      </Box>
    </Stack>
  );
}
